import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, GripHorizontalIcon, RotateCcwIcon, XIcon } from 'lucide-react';
export interface PanelPosition {
  x: number;
  y: number;
}
interface FloatingPanelProps {
  open: boolean;
  title: string;
  description?: string;
  /** Posición guardada. Si no se pasa, se usa la inicial por defecto. */
  position?: PanelPosition;
  onPositionChange?: (position: PanelPosition) => void;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
  footer?: React.ReactNode;
}
const DEFAULT_POSITION: PanelPosition = {
  x: 24,
  y: 96
};

/**
 * Panel flotante y arrastrable que no bloquea el contenido de detrás: los
 * cambios que se hacen dentro se ven en tiempo real en la tabla o la vista
 * previa. En móvil se comporta como una hoja inferior a pantalla completa.
 */
export function FloatingPanel({
  open,
  title,
  description,
  position,
  onPositionChange,
  onClose,
  width = 340,
  children,
  footer
}: FloatingPanelProps) {
  const [pos, setPos] = useState<PanelPosition>(position ?? DEFAULT_POSITION);
  const [collapsed, setCollapsed] = useState(false);
  const dragRef = useRef<{
    dx: number;
    dy: number;
  } | null>(null);
  useEffect(() => {
    if (position) setPos(position);
  }, [position?.x, position?.y]);
  useEffect(() => {
    if (!open) return;
    function move(event: PointerEvent) {
      if (!dragRef.current) return;
      // Se mantiene siempre dentro de la ventana visible.
      const next = {
        x: Math.min(Math.max(8, event.clientX - dragRef.current.dx), Math.max(8, window.innerWidth - width - 8)),
        y: Math.min(Math.max(8, event.clientY - dragRef.current.dy), window.innerHeight - 120)
      };
      setPos(next);
    }
    function up() {
      if (!dragRef.current) return;
      dragRef.current = null;
      onPositionChange?.(pos);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [open, pos, width, onPositionChange]);
  if (!open) return null;
  const header = <div className="flex cursor-grab items-start gap-2 border-b border-border px-4 py-3 active:cursor-grabbing sm:px-5" onPointerDown={event => {
    // Solo se arrastra desde la cabecera, nunca desde los controles.
    if ((event.target as HTMLElement).closest('button')) return;
    dragRef.current = {
      dx: event.clientX - pos.x,
      dy: event.clientY - pos.y
    };
  }} data-id="element-830" data-mp="1z1yurhh7m6xq">
      <GripHorizontalIcon className="mt-0.5 hidden h-4 w-4 flex-none text-muted-foreground sm:block" aria-hidden="true" data-id="element-831" data-mp="1g330k65cpsjw" />
      <div className="min-w-0 flex-1" data-id="element-832" data-mp="1batg35n09kp0">
        <h2 className="truncate text-sm font-semibold text-foreground" data-id="element-833" data-mp="nma4ag4qyps6">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-834" data-mp="io8p46afzalz">{description}</p>}
      </div>
      <div className="flex flex-none items-center gap-1" data-id="element-835" data-mp="2uaix460tqvqd">
        <button type="button" onClick={() => {
        setPos(DEFAULT_POSITION);
        onPositionChange?.(DEFAULT_POSITION);
      }} className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-foreground sm:flex" aria-label="Restaurar posición" data-id="element-836" data-mp="2u8zg2d7cggnj">
          <RotateCcwIcon className="h-4 w-4" data-id="element-837" data-mp="3d23bo444h3ks" />
        </button>
        <button type="button" onClick={() => setCollapsed(v => !v)} className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-foreground sm:h-9 sm:w-9" aria-label={collapsed ? 'Expandir panel' : 'Contraer panel'} data-id="element-838" data-mp="3i0n1iflu6zif">
          {collapsed ? <ChevronUpIcon className="h-4 w-4" data-id="element-839" data-mp="1wn24kzrdtbl3" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-840" data-mp="1wod6eu19o99k" />}
        </button>
        <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-foreground sm:h-9 sm:w-9" aria-label="Cerrar panel" data-id="element-841" data-mp="2cuyj5gvoornr">
          <XIcon className="h-4 w-4" data-id="element-842" data-mp="1y623axndyacl" />
        </button>
      </div>
    </div>;
  const body = collapsed ? null : <>
      <div className="max-h-[55vh] overflow-y-auto px-4 py-4 sm:max-h-[60vh] sm:px-5" data-id="element-843" data-mp="2vsix3swwu5tl">
        {children}
      </div>
      {footer && <div className="border-t border-border px-4 py-3 sm:px-5" data-id="element-844" data-mp="2luv1l0rhcuad">{footer}</div>}
    </>;
  return <>
      {/* Móvil: hoja inferior, sin arrastre. */}
      <div role="dialog" aria-label={title} className="fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border border-border bg-background shadow-2xl sm:hidden" data-id="element-845" data-mp="2ht00nxdgoix4">
        {header}
        {body}
      </div>

      {/* Escritorio: panel flotante y movible. */}
      <div role="dialog" aria-label={title} style={{
      left: pos.x,
      top: pos.y,
      width
    }} className="fixed z-40 hidden rounded-2xl border border-border bg-background shadow-2xl sm:block" data-id="element-846" data-mp="3i2r8uffnrwts">
        {header}
        {body}
      </div>
    </>;
}